import * as apply from 'fp-ts/Apply';
import * as array from 'fp-ts/Array';
import * as chainable from 'fp-ts/Chain';
import * as e from 'fp-ts/Either';
import * as fe from 'fp-ts/FromEither';
import { constant, flow, identity, pipe } from 'fp-ts/function';
import * as o from 'fp-ts/Option';
import * as oT from 'fp-ts/OptionT';
import { sign } from 'fp-ts/Ordering';
import * as remote from './remote.js';
/**
 * @category Instances
 */
export const URI = 'RemoteOption';
/**
 * @category Constructors
 */
export const initial = remote.initial;
/**
 * @category Constructors
 */
export const constInitial = constant(initial);
/**
 * @category Constructors
 */
export const pending = remote.pending;
/**
 * @category Constructors
 */
export const constPending = constant(pending);
/**
 * @category Constructors
 */
export const none = remote.done(o.none);
/**
 * @category Constructors
 */
export const constNone = constant(none);
/**
 * @category Constructors
 */
export const some = oT.some(remote.Pointed);
/**
 * @category Constructors
 */
export const of = some;
/**
 * @category Instances
 */
export const Pointed = {
    URI,
    of,
};
/**
 * @category Constructors
 */
export const zero = remote.zero;
/**
 * @category Instances
 */
export const Zero = {
    URI,
    zero,
};
/**
 * @category Refinements
 */
export const isInitial = remote.isInitial;
/**
 * @category Refinements
 */
export const isPending = remote.isPending;
/**
 * @category Refinements
 */
export const isNone = remote.exists(o.isNone);
/**
 * @category Refinements
 */
export const isSome = remote.exists(o.isSome);
/**
 * @category pattern matching
 */
export const match = (i, p, l, r) => remote.fold(i, p, o.fold(l, r));
/**
 * @internal
 */
const _match = (fa, onInitial, onPending, onNone, onSome) => match(onInitial, onPending, onNone, onSome)(fa);
/**
 * @category mapping
 */
export const map = oT.map(remote.Functor);
/**
 * @category Instances
 */
export const Functor = {
    URI,
    map: (fa, f) => pipe(fa, map(f)),
};
/**
 * @category utils
 */
export const ap = (fa) => (fab) => {
    if (isNone(fa))
        return isNone(fab) ? fab : fa;
    else if (isPending(fa))
        return isNone(fab) ? fab : fa;
    else if (isInitial(fa))
        return isNone(fab) || isPending(fab) ? fab : fa;
    else
        return isSome(fab)
            ? some(fab.value.value(fa.value.value))
            : fab;
};
/**
 * @category Instances
 */
export const Apply = {
    ...Functor,
    ap: (fab, fa) => pipe(fab, ap(fa)),
};
/**
 * @category Instances
 */
export const Applicative = {
    ...Apply,
    ...Pointed,
};
/**
 * @category sequencing
 */
export const chain = oT.chain(remote.Monad);
/**
 * @category Instances
 */
export const Chain = {
    ...Apply,
    chain: (fa, f) => pipe(fa, chain(f)),
};
/**
 * @category Instances
 */
export const Monad = {
    ...Chain,
    ...Applicative,
};
/**
 * @category folding
 */
export const reduce = (b, f) => (fa) => (isSome(fa) ? f(b, fa.value.value) : b);
/**
 * @category folding
 */
export const reduceRight = (b, f) => (fa) => (isSome(fa) ? f(fa.value.value, b) : b);
/**
 * @category folding
 */
export const foldMap = (M) => (f) => (fa) => (isSome(fa) ? f(fa.value.value) : M.empty);
/**
 * @category Instances
 */
export const Foldable = {
    URI,
    reduce: (fa, b, f) => pipe(fa, reduce(b, f)),
    reduceRight: (fa, b, f) => pipe(fa, reduceRight(b, f)),
    foldMap: (M) => (fa, f) => pipe(fa, foldMap(M)(f)),
};
/**
 * @category traversing
 */
export const traverse = (F) => (fab) => (f) => (isSome(f) ? F.map(fab(f.value.value), some) : F.of(f));
/**
 * @category traversing
 */
export const sequence = (F) => traverse(F)(identity);
/**
 * @category Instances
 */
export const Traversable = {
    ...Functor,
    ...Foldable,
    sequence,
    traverse: (F) => (f, fab) => pipe(f, traverse(F)(fab)),
};
/**
 * @category error handling
 */
export const alt = oT.alt(remote.Monad);
/**
 * @category Instances
 */
export const Alt = {
    ...Functor,
    alt: (fa, that) => pipe(fa, alt(that)),
};
/**
 * @category Instances
 */
export const Alternative = {
    ...Applicative,
    ...Alt,
    ...Zero,
};
/**
 * @category utils
 */
export const extend = (f) => (fa) => isSome(fa) ? some(f(fa)) : fa;
/**
 * @category Instances
 */
export const Extend = {
    ...Functor,
    extend: (wa, f) => pipe(wa, extend(f)),
};
/**
 * @category conversions
 */
export const fromEither = (ea) => e.fold(constNone, some)(ea);
/**
 * @category Instances
 */
export const FromEither = {
    URI,
    fromEither,
};
/**
 * @category utils
 */
export const apFirst = apply.apFirst(Apply);
/**
 * @category utils
 */
export const apSecond = apply.apSecond(Apply);
/**
 * @category do notation
 */
export const apS = apply.apS(Apply);
/**
 * @category do notation
 */
export const bind = chainable.bind(Chain);
/**
 * @category conversions
 */
export const fromOption = remote.of;
/**
 * @category conversions
 */
export const fromNullable = oT.fromNullable(remote.Pointed);
/**
 * @category conversions
 */
export const fromPredicate = oT.fromPredicate(remote.Pointed);
/**
 * @category conversions
 */
export const fromRemote = oT.fromF(remote.Functor);
/**
 * @category lifting
 */
export const fromEitherK = fe.fromEitherK(FromEither);
/**
 * @category lifting
 */
export const fromOptionK = oT.fromOptionK(remote.Pointed);
/**
 * @category lifting
 */
export const fromNullableK = oT.fromNullableK(remote.Pointed);
/**
 * @category lifting
 */
export const fromRemoteK = (f) => flow(f, fromRemote);
/**
 * @category filtering
 */
export const filter = (pred) => (rd) => isSome(rd) ? (pred(rd.value.value) ? rd : none) : rd;
/**
 * @category sequencing
 */
export const flatten = chain(identity);
/**
 * @category sequencing
 */
export const chainOptionK = oT.chainOptionK(remote.Monad);
/**
 * @category sequencing
 */
export const chainNullableK = oT.chainNullableK(remote.Monad);
/**
 * @category sequencing
 */
export const chainEitherK = fe.chainEitherK(FromEither, Chain);
/**
 * @category sequencing
 */
export const chainRemoteK = flow(fromRemoteK, chain);
/**
 * @category sequencing
 */
export const chainFirst = chainable.chainFirst(Chain);
/**
 * @category sequencing
 */
export const chainFirstEitherK = fe.chainFirstEitherK(FromEither, Chain);
/**
 * @category sequencing
 */
export const chainFirstOptionK = flow(fromOptionK, chainFirst);
/**
 * @category sequencing
 */
export const chainFirstRemoteK = flow(fromRemoteK, chainFirst);
/**
 * @category Destructors
 */
export const getOrElseW = (onNone) => (fa) => (isSome(fa) ? fa.value.value : onNone());
/**
 * @category Destructors
 */
export const getOrElse = (onNone) => (fa) => getOrElseW(onNone)(fa);
/**
 * @category pattern matching
 */
export const match3 = (onUnresolved, onNone, onSome) => match(onUnresolved, onUnresolved, onNone, onSome);
/**
 * @category Destructors
 */
export const toNullable = (ma) => isSome(ma) ? ma.value.value : null;
/**
 * @category Destructors
 */
export const toUndefined = (ma) => isSome(ma) ? ma.value.value : undefined;
/**
 * @category Destructors
 */
export const toOption = remote.fold(constant(o.none), constant(o.none), identity);
/**
 * @category Destructors
 */
export const toEither = (onInitial, onPending, onNone) => remote.fold(flow(onInitial, e.left), flow(onPending, e.left), e.fromOption(onNone));
/**
 * @category utils
 */
export const elem = (E) => (a) => (fa) => isSome(fa) && E.equals(fa.value.value, a);
/**
 * @category utils
 */
export const exists = (predicate) => (fa) => isSome(fa) && predicate(fa.value.value);
/**
 * @category Instances
 */
export const getEq = (EA) => ({
    equals: (x, y) => _match(x, () => isInitial(y), () => isPending(y), () => isNone(y), (ax) => isSome(y) && EA.equals(ax, y.value.value)),
});
/**
 * @category Instances
 */
export const getMonoid = (SA) => remote.getMonoid(o.getMonoid(SA));
const constLt = constant(-1);
const constEq = constant(0);
const constGt = constant(1);
/**
 * @category Instances
 */
export const getOrd = (OA) => ({
    ...getEq(OA),
    compare: (x, y) => sign(_match(x, () => _match(y, constEq, constLt, constLt, constLt), () => _match(y, constGt, constEq, constLt, constLt), () => _match(y, constGt, constGt, constEq, constLt), (xSome) => _match(y, constGt, constGt, constGt, (ySome) => OA.compare(xSome, ySome)))),
});
/**
 * @category Instances
 */
export const getShow = (SA) => ({
    show: match(() => 'initial', () => 'pending', () => `none`, (a) => `some(${SA.show(a)})`),
});
/**
 * @category traversing
 */
export const sequenceS = apply.sequenceS(Apply);
/**
 * @category traversing
 */
export const sequenceT = apply.sequenceT(Apply);
/**
 * @category traversing
 */
export const sequenceArray = array.sequence(Applicative);
/**
 * @category utils
 */
export const staleWhileRevalidate = (next) => (current) => pipe(next, match(() => current, () => (isInitial(current) ? next : current), () => next, () => next));
/**
 * @category utils
 */
export const staleIfError = (next) => (current) => pipe(next, match(() => current, () => (isInitial(current) || isNone(current) ? next : current), () => (isSome(current) ? current : next), () => next));
//# sourceMappingURL=remote-option.js.map